// ═══════════════════════════════════════════════════════════════
// PersonDetailPanel.js — Panneau détails personne (Family P2c)
// ═══════════════════════════════════════════════════════════════
// Collé à droite du graphe. Affiche les métadonnées + actions
// contextuelles : Éditer, Promouvoir admin2, Ajouter relation,
// Retirer de cohabitation, Fermer.
// ═══════════════════════════════════════════════════════════════

import React, { useMemo } from 'react';
import {
    X, Edit3, Shield, ShieldOff, Link2, UserMinus, Globe2, Calendar,
} from 'lucide-react';
import { promoteAdmin2, demoteAdmin2 } from '../../services/familyApi';
import { toast, confirm } from '../ui/Toast';

const PersonDetailPanel = ({
    person,
    relationships = [],
    persons = [],
    cohabitations = [],
    projectId,
    isDark,
    onClose,
    onEdit,
    onAddRelation,
    onRemoveFromCohabitation,
    onRefresh,
}) => {
    const surface = isDark ? '#15152a' : '#ffffff';
    const surfaceAlt = isDark ? '#1a1a2e' : '#f8fafc';
    const textPrimary = isDark ? '#e5e7eb' : '#1e293b';
    const textSecondary = isDark ? '#94a3b8' : '#64748b';
    const border = isDark ? '#2a2a44' : '#e2e8f0';

    // Une ligne par relation sémantique.
    // - CHILD_TYPES (enfant/pupille/filleul) : ignorés, redondants avec parental
    // - SIBLING_TYPES (frere/soeur/cousin…) : fusionnés par paire, label combiné
    // - Autres : affichés avec direction contextuelle
    const personRels = useMemo(() => {
        const byId = Object.fromEntries(persons.map((p) => [p.id, p]));
        const CHILD_TYPES = new Set(['enfant', 'pupille', 'filleul']);
        const SIBLING_TYPES = new Set(['frere', 'soeur', 'cousin', 'cousine', 'demi_frere', 'demi_soeur']);
        const PARENT_INVERSE = {
            pere: 'Enfant de', mere: 'Enfant de',
            grand_pere: 'Petit-enfant de', grand_mere: 'Petit-enfant de',
            tuteur_legal: 'Pupille de',
            parrain: 'Filleul·e de', marraine: 'Filleul·e de',
            oncle: 'Neveu·e de', tante: 'Neveu·e de',
            beau_pere: 'Bel-enfant de', belle_mere: 'Bel-enfant de',
        };
        const seen = new Map();
        const siblingByOther = new Map(); // otherId → { labels: Set, other, representativeType }

        relationships
            .filter((r) => {
                if (CHILD_TYPES.has(r.type)) return false;
                return r.person_a_id === person.id || r.person_b_id === person.id;
            })
            .forEach((r) => {
                const isSource = r.person_a_id === person.id;
                const otherId = isSource ? r.person_b_id : r.person_a_id;
                const other = byId[otherId];
                if (!other) return;

                if (SIBLING_TYPES.has(r.type)) {
                    // Fusion par autre personne — combine frere+soeur en un seul affichage
                    if (!siblingByOther.has(otherId)) {
                        siblingByOther.set(otherId, { labels: new Set(), other, type: r.type });
                    }
                    siblingByOther.get(otherId).labels.add(r.type_label || r.type);
                    return;
                }

                const displayLabel = isSource
                    ? `${r.type_label || r.type} de`
                    : (PARENT_INVERSE[r.type] || `${r.type_label || r.type} (reçu)`);

                const key = `${otherId}:${r.type}:${isSource ? 'out' : 'in'}`;
                if (!seen.has(key)) seen.set(key, { ...r, other, displayLabel, isSource });
            });

        // Ajoute les lignes sibling fusionnées
        siblingByOther.forEach(({ labels, other, type }, otherId) => {
            const combined = [...labels].join(' / ');
            seen.set(`sibling:${otherId}`, {
                id: `sibling-${otherId}`,
                type,
                other,
                displayLabel: combined,
                isSource: true,
                source: 'manual',
            });
        });

        const order = { conjoint: 0, pere: 1, mere: 1, grand_pere: 2, grand_mere: 2, frere: 3, soeur: 3, cousin: 4, cousine: 4 };
        return [...seen.values()].sort((a, b) => (order[a.type] ?? 9) - (order[b.type] ?? 9));
    }, [relationships, persons, person]);

    const personCohabs = useMemo(() => {
        return cohabitations.filter((co) =>
            (co.members || []).some((m) => m.person_id === person.id)
        );
    }, [cohabitations, person]);

    const handleTogglePromote = async () => {
        const isPromoting = !person.is_admin2;
        const label = isPromoting ? 'Promouvoir admin 2' : 'Retirer le statut admin 2';
        const ok = await confirm(
            isPromoting
                ? `Désigner « ${person.full_name} » comme admin 2 du projet ?`
                : `Retirer le statut admin 2 à « ${person.full_name} » ?`,
            { confirmText: label }
        );
        if (!ok) return;
        try {
            if (isPromoting) await promoteAdmin2(projectId, person.id);
            else await demoteAdmin2(projectId, person.id);
            toast.success(isPromoting ? 'Promu admin 2' : 'Statut admin 2 retiré');
            onRefresh?.();
        } catch (e) {
            toast.error(`Échec : ${e.message}`);
        }
    };

    const handleRemoveFromCohab = async (co) => {
        const ok = await confirm(
            `Retirer « ${person.full_name} » de la cohabitation « ${co.label} » ?`,
            { confirmText: 'Retirer' }
        );
        if (!ok) return;
        onRemoveFromCohabitation?.(co, person);
    };

    return (
        <div
            style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: 340,
                background: surface,
                borderLeft: `1px solid ${border}`,
                display: 'flex',
                flexDirection: 'column',
                zIndex: 10,
                boxShadow: isDark
                    ? '-12px 0 32px rgba(0,0,0,0.35)'
                    : '-12px 0 32px rgba(15,23,42,0.08)',
            }}
        >
            {/* Header */}
            <div style={{
                padding: '18px 20px',
                borderBottom: `1px solid ${border}`,
                display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10,
            }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{
                        fontSize: 15, fontWeight: 600, color: textPrimary,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }} title={person.full_name}>
                        {person.full_name}
                    </div>
                    {person.role && (
                        <div style={{ fontSize: 12, color: textSecondary, marginTop: 2 }}>
                            {person.role}
                        </div>
                    )}
                </div>
                <button
                    onClick={onClose}
                    style={{ background: 'transparent', border: 'none', color: textSecondary, cursor: 'pointer', padding: 4, borderRadius: 6 }}
                    title="Fermer"
                >
                    <X size={18} />
                </button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
                {/* Métadonnées */}
                <Section title="Informations" textSecondary={textSecondary}>
                    <MetaRow icon={<Calendar size={13} />} textSecondary={textSecondary}>
                        {person.birth_date
                            ? new Date(person.birth_date).toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })
                            : <em style={{ opacity: 0.6 }}>Non renseignée</em>}
                    </MetaRow>
                    <MetaRow icon={<Globe2 size={13} />} textSecondary={textSecondary}>
                        {(person.nationalities || []).length
                            ? person.nationalities.join(' · ')
                            : <em style={{ opacity: 0.6 }}>Aucune nationalité</em>}
                    </MetaRow>
                    {person.is_admin2 && (
                        <MetaRow icon={<Shield size={13} />} textSecondary="#a855f7">
                            <span style={{ color: '#a855f7', fontWeight: 500 }}>Admin 2 du projet</span>
                            {!person.user_account_id && (
                                <span style={{ fontSize: 10, color: textSecondary, marginLeft: 6 }}>
                                    (compte non lié)
                                </span>
                            )}
                        </MetaRow>
                    )}
                </Section>

                {/* Relations */}
                <Section title={`Relations (${personRels.length})`} textSecondary={textSecondary}>
                    {personRels.length === 0 ? (
                        <div style={{ fontSize: 12, color: textSecondary, fontStyle: 'italic' }}>
                            Aucune relation renseignée.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {personRels.map((r) => (
                                <div
                                    key={r.id}
                                    style={{
                                        fontSize: 12, color: textPrimary,
                                        padding: '6px 10px', background: surfaceAlt,
                                        border: `1px solid ${border}`, borderRadius: 8,
                                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    }}
                                >
                                    <span>
                                        <span style={{ color: textSecondary }}>{r.displayLabel || r.type_label || r.type}</span>
                                        {r.other && <> <strong>{r.other.full_name}</strong></>}
                                    </span>
                                    {r.source === 'ai_detected' && (
                                        <span style={{
                                            fontSize: 9, color: '#f59e0b',
                                            background: '#f59e0b18', padding: '1px 5px',
                                            borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.4,
                                        }} title="Détectée par l'IA, non validée">
                                            ia
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </Section>

                {/* Cohabitations */}
                {personCohabs.length > 0 && (
                    <Section title={`Cohabitations (${personCohabs.length})`} textSecondary={textSecondary}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {personCohabs.map((co) => {
                                const member = (co.members || []).find((m) => m.person_id === person.id);
                                return (
                                    <div
                                        key={co.id}
                                        style={{
                                            fontSize: 12, color: textPrimary,
                                            padding: '8px 10px', background: surfaceAlt,
                                            border: `1px solid ${border}`, borderRadius: 8,
                                        }}
                                    >
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <strong>{co.label}</strong>
                                            {co.is_primary && (
                                                <span style={{
                                                    fontSize: 9, color: '#10b981',
                                                    background: '#10b98118', padding: '1px 5px',
                                                    borderRadius: 4, textTransform: 'uppercase', letterSpacing: 0.4,
                                                }}>primaire</span>
                                            )}
                                        </div>
                                        <div style={{ fontSize: 11, color: textSecondary, marginTop: 2 }}>
                                            {member?.role_in_cohabitation && <>{member.role_in_cohabitation} · </>}
                                            {member?.share_ratio != null && `${Math.round(member.share_ratio * 100)}%`}
                                        </div>
                                        <button
                                            onClick={() => handleRemoveFromCohab(co)}
                                            style={{
                                                marginTop: 6, fontSize: 11, color: '#ef4444',
                                                background: 'transparent', border: 'none', cursor: 'pointer',
                                                padding: 0, display: 'inline-flex', alignItems: 'center', gap: 4,
                                            }}
                                        >
                                            <UserMinus size={11} /> Retirer de cette cohabitation
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </Section>
                )}
            </div>

            {/* Footer actions */}
            <div style={{
                padding: '14px 18px',
                borderTop: `1px solid ${border}`,
                background: surfaceAlt,
                display: 'flex', flexDirection: 'column', gap: 6,
            }}>
                <ActionButton icon={<Edit3 size={13} />} onClick={onEdit} primary>
                    Éditer
                </ActionButton>
                <ActionButton icon={<Link2 size={13} />} onClick={onAddRelation}>
                    Ajouter une relation
                </ActionButton>
                <ActionButton
                    icon={person.is_admin2 ? <ShieldOff size={13} /> : <Shield size={13} />}
                    onClick={handleTogglePromote}
                    accent="#a855f7"
                >
                    {person.is_admin2 ? 'Retirer admin 2' : 'Promouvoir admin 2'}
                </ActionButton>
            </div>
        </div>
    );
};

const Section = ({ title, children, textSecondary }) => (
    <div style={{ marginBottom: 18 }}>
        <div style={{
            fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.6,
            color: textSecondary, marginBottom: 8,
        }}>
            {title}
        </div>
        {children}
    </div>
);

const MetaRow = ({ icon, children, textSecondary }) => (
    <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 12, padding: '4px 0', color: textSecondary,
    }}>
        <span style={{ color: textSecondary, display: 'flex', alignItems: 'center' }}>{icon}</span>
        <span style={{ color: 'inherit' }}>{children}</span>
    </div>
);

const ActionButton = ({ icon, onClick, children, primary, accent }) => {
    const color = primary ? '#1D4ED8' : (accent || 'currentColor');
    const bg = primary ? '#1D4ED8' : 'transparent';
    const fg = primary ? '#fff' : color;
    return (
        <button
            onClick={onClick}
            style={{
                background: bg,
                color: fg,
                border: primary ? 'none' : `1px solid ${color}44`,
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                transition: 'background 0.15s, opacity 0.15s',
            }}
            onMouseEnter={(e) => { if (!primary) e.currentTarget.style.background = `${color}15`; }}
            onMouseLeave={(e) => { if (!primary) e.currentTarget.style.background = 'transparent'; }}
        >
            {icon}
            {children}
        </button>
    );
};

export default PersonDetailPanel;
